import { SERVICES, PROJECTS, REVIEWS, COMPANY_INFO } from '../data/staticData';

const BASE_URL = '/_api/public/v2/content';

async function fetchCmsData(path) {
  try {
    const res = await fetch(`/_api/public/v2/content/${path}`);
    if (res.ok) {
      const data = await res.json();
      return data.items || data;
    }
  } catch {}
  try {
    const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/${path}`);
    if (res.ok) {
      const data = await res.json();
      return data.items || data;
    }
  } catch {}
  return null;
}

/**
 * Fetch services from CMS or fallback to static list
 */
export async function getServices() {
  try {
    const items = await fetchCmsData('services');
    if (Array.isArray(items) && items.length > 0) {
      return items.map((item) => {
        const slugKey = item.service_slug || item.slug || item.id;
        const fallback = SERVICES.find(s => s.slug === slugKey || s.id === slugKey);
        return {
          id: slugKey,
          slug: slugKey,
          title: item.title || fallback?.title,
          category: item.category || fallback?.category || 'Nyt tag',
          description: item.description || fallback?.description || '',
          content: (item.content && item.content.length > 500) ? item.content : (fallback?.content || item.content || ''),
          image: Array.isArray(item.image) && item.image.length > 0 ? item.image[0] : (item.image || fallback?.image || ''),
          meta_title: item.meta_title || fallback?.meta_title || `${item.title} - Dansk Tagbyg ApS`,
          meta_description: item.meta_description || fallback?.meta_description || item.description || ''
        };
      });
    }
  } catch (err) {
    console.warn('CMS fetch /services warning:', err);
  }
  return SERVICES;
}

/**
 * Fetch single service by slug
 */
export async function getServiceBySlug(slug) {
  const allServices = await getServices();
  return allServices.find((s) => s.slug === slug || s.id === slug) || SERVICES.find(s => s.slug === slug);
}

/**
 * Fetch projects from CMS
 */
export async function getProjects() {
  try {
    const items = await fetchCmsData('projects');
    if (Array.isArray(items) && items.length > 0) {
      return items.map((item) => {
        const slugKey = item.project_slug || (item.slug && item.slug._) || item.slug || item.id;
        const fallback = PROJECTS.find(
          (p) => p.slug?.toLowerCase() === slugKey?.toLowerCase() ||
                 p.id?.toLowerCase() === slugKey?.toLowerCase() ||
                 p.title?.toLowerCase() === item.title?.toLowerCase()
        );

        // Collect and deduplicate all images from both item.image and item.gallery_images
        const rawImages = [
          ...(Array.isArray(item.image) ? item.image : (item.image ? [item.image] : [])),
          ...(Array.isArray(item.gallery_images) ? item.gallery_images : (item.gallery_images ? [item.gallery_images] : []))
        ];
        const uniqueImages = [...new Set(rawImages.filter(Boolean))];
        const mainImage = uniqueImages[0] || fallback?.image || '';
        const galleryImages = uniqueImages.length > 0
          ? uniqueImages
          : (fallback?.gallery_images || (mainImage ? [mainImage] : []));

        // Category determination
        const roofType = item.roof_type || fallback?.roof_type || '';
        const category = item.category || fallback?.category || (
          /vindu|dør|energi|isolering/i.test(roofType) ? 'Energiløsninger' : 'Tag'
        );

        // Parse included bullet points from HTML <li> if present
        let included = fallback?.included || [];
        if (typeof item.content === 'string' && item.content.includes('<li>')) {
          const matches = [...item.content.matchAll(/<li>(.*?)<\/li>/g)];
          if (matches.length > 0) {
            included = matches.map((m) => m[1].replace(/<[^>]*>?/gm, '').trim()).filter(Boolean);
          }
        }

        // Generate clean plain text description
        let description = fallback?.description || '';
        if (typeof item.content === 'string') {
          const plain = item.content.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
          if (plain) {
            description = plain.length > 200 ? plain.slice(0, 200).trim() + '...' : plain;
          }
        }

        return {
          id: slugKey,
          slug: slugKey,
          title: item.title || fallback?.title,
          category,
          m2_price: item.m2_price || fallback?.m2_price || '',
          roof_type: roofType,
          location: item.location || fallback?.location || '',
          content: item.content || fallback?.content || description,
          description,
          included,
          image: mainImage,
          gallery_images: galleryImages,
          rawItem: item
        };
      });
    }
  } catch (err) {
    console.warn('CMS fetch /projects warning:', err);
  }
  return PROJECTS;
}

/**
 * Fetch reviews from CMS
 */
export async function getReviews() {
  try {
    const items = await fetchCmsData('reviews');
    if (Array.isArray(items) && items.length > 0) {
      return items.map((item) => ({
        id: item.id,
        author: item.author,
        location: item.location || '',
        rating: item.rating || 5,
        date: item.date || '',
        quote: typeof item.quote === 'string' ? item.quote.replace(/<[^>]*>?/gm, '') : ''
      }));
    }
  } catch (err) {
    console.warn('CMS fetch /reviews warning:', err);
  }
  return REVIEWS;
}

/**
 * Fetch gallery for a specific service slug via CMS technical reference
 */
export async function getGalleryForService(serviceSlug) {
  try {
    const items = await fetchCmsData(`galleries?service_slug=${encodeURIComponent(serviceSlug)}`);
    if (Array.isArray(items) && items.length > 0 && items[0].images) {
      return Array.isArray(items[0].images) ? items[0].images : [items[0].images];
    }
  } catch (err) {
    console.warn('CMS fetch /galleries warning:', err);
  }
  // Fallback to project or service default image
  const service = SERVICES.find(s => s.slug === serviceSlug);
  return service ? [service.image] : [];
}

/**
 * Helper to submit form to https://api.dev.auxo.dkwebform/submit
 */
export async function submitWebform(formDataObj) {
  try {
    const isFormData = typeof FormData !== 'undefined' && formDataObj instanceof FormData;
    const response = await fetch('/_api/webform/submit', {
      method: 'POST',
      ...(isFormData ? {} : { headers: { 'Content-Type': 'application/json' } }),
      body: isFormData ? formDataObj : JSON.stringify(formDataObj)
    });
    const result = await response.json();
    return { ok: response.ok, result };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}
